import clsx from 'clsx';
import { useTheme, type Theme } from './useTheme.js';
import './ThemeToggle.css';

const DEFAULT_LABELS: Readonly<Record<Theme, string>> = {
  light: 'Light',
  system: 'Auto',
  dark: 'Dark',
};

const GLYPHS: Readonly<Record<Theme, string>> = {
  light: '☀',
  system: '◐',
  dark: '☾',
};

const ORDER: ReadonlyArray<Theme> = ['light', 'system', 'dark'];

export interface ThemeToggleProps {
  className?: string;
  /** Optional localized button labels. Falls back to English defaults. */
  labels?: Partial<Readonly<Record<Theme, string>>>;
  /** Optional localized aria-label for the radiogroup. */
  ariaLabel?: string;
}

/**
 * Three-state theme switch: light / system / dark.
 * Persists the pick in localStorage via useTheme().
 * Intended for settings screens, dev tools, and the pilot review pages.
 */
export function ThemeToggle({ className, labels, ariaLabel }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const resolved = { ...DEFAULT_LABELS, ...labels };
  return (
    <div
      className={clsx('fluent-theme-toggle', className)}
      role="group"
      aria-label={ariaLabel ?? 'Theme'}
    >
      {ORDER.map((value) => (
        <button
          key={value}
          type="button"
          className="fluent-theme-toggle__btn"
          data-selected={theme === value || undefined}
          onClick={() => setTheme(value)}
          aria-pressed={theme === value}
          title={resolved[value]}
        >
          <span aria-hidden="true" className="fluent-theme-toggle__glyph">
            {GLYPHS[value]}
          </span>
          <span className="fluent-theme-toggle__label">{resolved[value]}</span>
        </button>
      ))}
    </div>
  );
}
